import { useEffect } from "react";
import { Link } from "react-router-dom";
import LegalLayout from "@/components/LegalLayout";

export default function MedicalDisclaimer() {
  useEffect(() => {
    document.title = "Medical Disclaimer · Stable-ish";
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalLayout
      title="Medical Disclaimer"
      eyebrow="§ legal · medical disclaimer"
      lastUpdated="Effective Date: May 26, 2026 · Last Updated: July 19, 2026"
    >
      <h2>Important Notice</h2>
      <p>
        Stable-ish is a product of <strong>Stable-ish LLC</strong>. It is an
        educational clinical reference tool designed primarily for emergency
        department and critical care registered nurses and other trained
        healthcare professionals.
      </p>
      <p>
        Stable-ish is not a medical device and is not intended to diagnose,
        treat, cure, prevent, monitor, or manage any disease or medical
        condition.
      </p>
      <p>
        By using Stable-ish, you acknowledge and agree to the terms of this
        Medical Disclaimer.
      </p>

      <h2>Educational and Informational Use Only</h2>
      <p>
        The information provided through Stable-ish is intended solely for
        educational and informational purposes.
      </p>
      <p>
        Content generated by the Service is designed to assist users in
        accessing clinical knowledge and reference information but is not a
        substitute for professional training, clinical judgment, or
        independent decision-making.
      </p>

      <h2>No Medical Advice</h2>
      <p>Stable-ish does not provide:</p>
      <ul>
        <li>Medical advice</li>
        <li>Nursing advice</li>
        <li>Pharmacy advice</li>
        <li>Legal advice</li>
        <li>Diagnosis</li>
        <li>Treatment recommendations</li>
        <li>Professional consultation</li>
      </ul>
      <p>
        No provider-patient relationship, nurse-patient relationship,
        physician-patient relationship, or other healthcare relationship is
        created through use of the Service.
      </p>

      <h2>AI-Generated Content</h2>
      <p>Stable-ish uses artificial intelligence technologies to generate responses.</p>
      <p>AI-generated content may:</p>
      <ul>
        <li>Be inaccurate</li>
        <li>Be incomplete</li>
        <li>Be outdated</li>
        <li>Contain errors</li>
        <li>Omit relevant information</li>
        <li>Misinterpret user input</li>
      </ul>
      <p>
        Users must independently verify all information before relying upon it
        in any educational, professional, or clinical setting.
      </p>

      <h2>Clinical Judgment Required</h2>
      <p>Users remain solely responsible for:</p>
      <ul>
        <li>Patient assessment</li>
        <li>Clinical decision-making</li>
        <li>Medication administration</li>
        <li>Interpretation of diagnostic studies</li>
        <li>Verification of dosing information</li>
        <li>Compliance with employer policies and procedures</li>
        <li>Compliance with applicable laws, regulations, and licensing requirements</li>
      </ul>
      <p>
        Stable-ish must never be used as a substitute for professional
        judgment, clinical expertise, or consultation with appropriate
        healthcare professionals.
      </p>

      <h2>Verification of Information</h2>
      <p>
        Users should independently verify information using appropriate
        sources, including:
      </p>
      <ul>
        <li>Hospital policies and procedures</li>
        <li>Clinical practice guidelines</li>
        <li>Pharmacists</li>
        <li>Physicians and advanced practice providers</li>
        <li>Manufacturer prescribing information</li>
        <li>Regulatory guidance</li>
        <li>Other authoritative clinical references</li>
      </ul>
      <p>
        The Service should never be relied upon as the sole source of
        information for patient care decisions.
      </p>

      <h2>Uploaded Images, Documents, and Recordings</h2>
      <p>
        Stable-ish may allow users to submit images, ECGs, monitor strips,
        screenshots, documents, or audio recordings for analysis.
      </p>
      <p>Stable-ish does not independently verify:</p>
      <ul>
        <li>Image quality</li>
        <li>Completeness</li>
        <li>Authenticity</li>
        <li>Calibration</li>
        <li>Orientation</li>
        <li>Accuracy of uploaded materials</li>
        <li>Clinical significance of uploaded materials</li>
      </ul>
      <p>
        Any analysis, interpretation, or educational content generated from
        uploaded materials must be independently reviewed and verified by the
        user.
      </p>
      <p>
        Stable-ish is not intended to provide definitive ECG interpretation,
        diagnosis, or treatment recommendations.
      </p>

      <h2>Emergency and Time-Critical Situations</h2>
      <p>
        Stable-ish should not be relied upon as the sole source of information
        during emergencies, resuscitations, critical patient events, or other
        time-sensitive clinical situations.
      </p>
      <p>
        Patient care decisions should be based upon professional training,
        clinical assessment, institutional protocols, and consultation with
        appropriate healthcare professionals.
      </p>

      <h2>No Guarantee of Accuracy</h2>
      <p>
        While reasonable efforts are made to provide useful and current
        information, Stable-ish makes no representations or warranties
        regarding the accuracy, completeness, reliability, availability, or
        suitability of any content provided through the Service.
      </p>
      <p>
        Medical knowledge, medications, guidelines, protocols, and standards
        of care change over time.
      </p>
      <p>Information available through the Service may become outdated.</p>

      <h2>User Responsibility</h2>
      <p>
        Users assume full responsibility for evaluating, interpreting, and
        applying any information obtained through Stable-ish.
      </p>
      <p>
        Any reliance on information provided through the Service is at the
        user&apos;s own risk.
      </p>
      <p>
        Users remain solely responsible for all professional decisions,
        actions, and patient care outcomes.
      </p>

      <h2>Contact Information</h2>
      <p>
        <strong>General Questions and Support:</strong>
        <br />
        <a href="mailto:support@stableish.me">support@stableish.me</a>
      </p>
      <p>
        <strong>Privacy Questions and Data Requests:</strong>
        <br />
        <a href="mailto:privacy@stableish.me">privacy@stableish.me</a>
      </p>

      <h2>Related Documents</h2>
      <p>
        This Medical Disclaimer is part of your agreement with Stable-ish.
        Please also review our{" "}
        <Link to="/terms" className="text-[#FF2A2A]">Terms of Service</Link>{" "}
        and{" "}
        <Link to="/privacy" className="text-[#FF2A2A]">Privacy Policy</Link>.
      </p>
    </LegalLayout>
  );
}
